/**
 * What a client sees of the Data Bank in the portal (All dossiers): the dossiers filed for their company, their own
 * certificates, and Makin's accreditation certificates. Nothing else of the Data Bank reaches a client.
 */

/** Dossiers as filed with the authority, for the client's own company. */
export const CLIENT_DOSSIER_SECTIONS = ['submitted_dossiers'] as const;

/** Certificates issued for the client's own company. */
export const CLIENT_CERTIFICATE_SECTIONS = ['registration', 'fsc_neuta', 'fsc_herbal'] as const;

/** Makin's own certificates (WHO-GMP, ISO, …): the same for every client. */
export const COMPANY_CERTIFICATE_SECTIONS = ['accreditation'] as const;

/** Artwork and product images Makin keeps for the client's own company. */
export const CLIENT_MEDIA_SECTIONS = ['approved_artworks', 'product_3d_images'] as const;

/** The client's Free Sale Certificate sections. */
export const CLIENT_FSC_SECTIONS = ['fsc_neuta', 'fsc_herbal'] as const;

// ---- Registration certificate validity ----------------------------------------------------------------------------

/** A registration certificate this close to its expiry date counts as expiring, and reminders go out. */
export const RC_EXPIRY_WARNING_DAYS = 90;

/** `undated`: the issue and expiry dates have not been recorded yet. */
export type RcStatus = 'valid' | 'expiring' | 'expired' | 'undated';

/** A dossier's registration certificate state: its most current certificate's status, or `missing`. */
export type DossierRcStatus = RcStatus | 'missing';

export interface RcValidityDto {
  issueDate: string | null;
  expiryDate: string | null;
  status: RcStatus;
}

/** Whole days from `from` to `to` (both `YYYY-MM-DD`). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** A certificate is valid through its expiry date and expiring within the last 90 days of it. */
export function rcStatus(expiryDate: string | null | undefined, today: string): RcStatus {
  if (!expiryDate) return 'undated';
  if (expiryDate < today) return 'expired';
  return daysBetween(today, expiryDate) <= RC_EXPIRY_WARNING_DAYS ? 'expiring' : 'valid';
}

/** What the client sends after uploading the file: which dossier it is for, and its dates. */
export interface ClientRcUploadInput {
  /** A filed dossier in the Data Bank, or… */
  dossierEntryId?: string | null;
  /** …a dossier Makin released through the portal. Exactly one of the two. */
  requestId?: string | null;
  fileId: string;
  issueDate: string;
  expiryDate: string;
}

export interface ClientLibraryDocumentDto {
  /** The Data Bank entry. */
  id: string;
  name: string;
  country: string | null;
  product: string | null;
  addedAt: string;
  /** Null when the entry is a link. */
  file: {
    id: string;
    contentType: string;
    sizeBytes: number | null;
    /** Still in Google Drive: it opens the Drive file, and cannot be downloaded directly. */
    inDrive: boolean;
  } | null;
  linkUrl: string | null;
  /** Registration certificates only: issue and expiry dates and what they mean today. */
  validity: RcValidityDto | null;
}

/**
 * A dossier filed for the company, with the registration certificates that belong to it. Either a Data Bank filed
 * dossier (its file is on the row), or a dossier Makin released through the portal (`request`; its files are on the
 * request page).
 */
export interface ClientFiledDossierDto extends ClientLibraryDocumentDto {
  request: { id: string; code: string; version: number } | null;
  /**
   * Registration certificates for this dossier's product and country: those uploaded for it, plus Data Bank ones
   * paired by the product named in the file names. Also listed under Certificates. Current ones first.
   */
  registrationCertificates: ClientLibraryDocumentDto[];
  rcStatus: DossierRcStatus;
}

export interface ClientLibrarySectionDto {
  key: string;
  label: string;
  /** Makin's own certificates rather than the client's. */
  companyWide: boolean;
  items: ClientLibraryDocumentDto[];
}

export interface ClientLibraryDto {
  dossiers: ClientFiledDossierDto[];
  /**
   * Client certificate sections first, then Makin's accreditation certificates. Always every section, possibly empty.
   * Registration certificates lists every one, including those also shown with their filed dossier.
   */
  certificates: ClientLibrarySectionDto[];
  /** Approved artworks and 3D images of commercial products, for the client's own company. Always every section. */
  media: ClientLibrarySectionDto[];
}

/** One query on the client's Queries list, across their dossiers. */
export interface ClientQueryRowDto {
  requestId: string;
  code: string;
  productName: string;
  country: string;
  /** Query 1, 2, … on its dossier. */
  number: number;
  comment: string;
  requestedAt: string;
  /** Waiting for Makin's compliance. */
  open: boolean;
  /** When Makin answered it (compliance, or a version for queries from before compliance). */
  answeredAt: string | null;
}

/** The counts on the client dashboard. */
export interface ClientDashboardSummaryDto {
  /** Dossiers filed for the company: the Data Bank's and those Makin released through the portal. */
  submittedDossiers: number;
  /** Requisitions the client sent through the portal: all received by Makin, and by state. */
  requisitions: { received: number; underProcess: number; ready: number; cancelled: number };
  /** Queries the client raised on released dossiers: open until Makin sends its compliance, then answered. */
  queries: { total: number; open: number; answered: number };
  /** Free Sale Certificates on file for the company: all, and FSC Nutra and FSC Herbal. */
  fsc: { available: number; nutra: number; herbal: number };
  /** Approved artworks and 3D images of commercial products on file for the company. */
  media: { artworks: number; images: number };
  /**
   * Registration certificates on file for the company's dossiers (Data Bank and client uploads), filed dossiers still
   * without one, and dossiers whose certificate expires within 90 days or has expired.
   */
  certificates: { onFile: number; pending: number; renewalDue: number };
  /** Samples asked for with the client's requisitions: all, not yet delivered (cancelled ones aside), and on the way. */
  samples: { total: number; open: number; onTheWay: number };
}
